import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import { getFetchError } from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { TypographyStyles } from '@/styles'
import { Alert, Card, CardColors, Text } from '@/ui'
import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'
import {
  TAX_PROFILE_STEP,
  useTaxProfileSteps,
} from '@/features/taxes/taxes-profile/form-flow/helpers'
import { TaxProfileFormFooter } from '@/features/taxes/taxes-profile/form-flow/TaxProfileFormFooter'
import { FilingStatusSubForm } from '@/features/taxes/taxes-profile/form-flow/FilingStatusSubForm'

import { selectActiveQuarterlyTaxEstimateDetails } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

// TODO: Migrate to V2
import { getFieldName } from '@/base'

/**
 * Entry points that live on the estimate-breakdown page rely on Back returning
 * there. Callers reached from elsewhere pass their own origin in router state.
 */
const getReturnTo = (state: unknown): string | null =>
  typeof state === 'object' &&
  state !== null &&
  'returnTo' in state &&
  typeof state.returnTo === 'string'
    ? state.returnTo
    : null

export const RecalculationAboutYouForm = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { state: locationState } = useLocation()
  const fp = useReselector(getFinancialProfile)
  const activeQuarterDetails = useReselector(
    selectActiveQuarterlyTaxEstimateDetails
  )

  const currentQuarter = activeQuarterDetails?.taxQuarter
  const currentYear = activeQuarterDetails?.taxYear
  const error = useReselector(
    getFetchError,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id)
  )
  const { goToNextStep } = useTaxProfileSteps(TAX_PROFILE_STEP.aboutYou)

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      filingStatusSame: undefined as boolean | undefined,
      filingStatus: fp?.filingStatus,
    },
    onSubmit: async ({ filingStatusSame, filingStatus }) => {
      const res = await dispatch(
        updateFinancialProfile(fp?.id, {
          ...((filingStatusSame === false || !fp?.filingStatus) && {
            filingStatus,
          }),
          // Clear spouse fields if not filing jointly
          ...(filingStatusSame === false &&
            filingStatus !== 'married_filing_jointly' && {
              spouseWIncomeInCents: null,
              spouseFederalWithholdingInCents: null,
              spouseStateWithholdingInCents: null,
              spouseIndividualIncomeInCents: null,
            }),
        })
      )

      if (res) {
        goToNextStep(res)
      }
    },
  })

  const { values, setFieldValue, submitForm, isValid, isSubmitting } = formik

  // If filingStatusSame toggled to true reset the filingStatus change
  useEffect(() => {
    if (values.filingStatusSame) {
      setFieldValue(
        getFieldName<typeof values>('filingStatus'),
        fp?.filingStatus
      )
    }
  }, [fp?.filingStatus, setFieldValue, values.filingStatusSame])

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col gap-4">
        <Text
          variant={TypographyStyles.DISPLAY_LG}
          className="text-center w-full"
        >
          Adjust your tax information
        </Text>
        <Text variant={TypographyStyles.BODY_LG} className="text-center w-full">
          In order to receive a new federal tax estimate, please make changes to
          the details that seem off.
        </Text>

        {error && (
          <Alert
            className="w-full"
            variant="error"
            description={error.message}
          />
        )}

        <Card color={CardColors.LEVEL_0} className="flex flex-col gap-4 p-4">
          <FilingStatusSubForm fp={fp} />
        </Card>

        <TaxProfileFormFooter
          submitOrContinue={submitForm}
          goToPreviousStep={() =>
            navigate(
              getReturnTo(locationState) ??
                `/taxes/quarterly/${currentYear}-${currentQuarter}/estimate-breakdown`
            )
          }
          continueDisabled={!isValid || isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </FormikProvider>
  )
}
